import { Ellipsis, ListTodo } from 'lucide-react';
import { useEffect, useState } from 'react';

import { type FocusSession, focusReview, focusTotals } from '../../shared/focus';
import type { LocalRecord, Note, Task } from '../../shared/model';
import { taskPersonName } from '../../shared/productivity';
import { isAvailableRecord } from '../../shared/trash';

import { setTaskDone } from '../lib/placements';
import { notePath, RouteLink, taskPath } from '../lib/routes';
import { taskStone } from '../lib/stones';
import { type TaskFilter, taskListPage, taskPageSize } from '../lib/task-list';
import { FocusHistoryDialog } from './FocusHistory';
import { StatusBadge, TaskBadges } from './StatusBadge';
import { PebbleButton } from './Stones';
import {
  ActionIconButton,
  Button,
  ButtonLink,
  ControlPopover,
  IconButton,
  SearchField,
  Select,
} from './ui';

export function TasksTable({
  records,
  save,
  deviceId,
}: {
  records: LocalRecord[];
  save: (record: LocalRecord, value: Task) => void;
  /** Completing a running task by its pebble ends the session on its own device. */
  deviceId: string;
}) {
  const [filter, setFilter] = useState<TaskFilter>('active');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [reviewTaskId, setReviewTaskId] = useState<string>();
  const reviewTasks = focusReview(records).taskIds;
  const result = taskListPage(records, filter, search, page);

  useEffect(() => {
    setPage(result.page);
  }, [result.page]);

  const notes = new Map(
    records
      .filter(r => r.kind === 'note' && isAvailableRecord(r))
      .map(r => [r.id, r.value as Note]),
  );

  return (
    <section className="tasks-collection" aria-label="Task collection">
      <div className="tasks-toolbar">
        <SearchField
          label="Search tasks"
          value={search}
          onChange={value => {
            setSearch(value);
            setPage(1);
          }}
        />
        <div className="tasks-status-filter">
          <Select
            aria-label="Task status"
            value={filter}
            onChange={e => {
              setFilter(e.target.value as TaskFilter);
              setPage(1);
            }}
          >
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="dropped">Dropped</option>
            <option value="all">All</option>
            <option value="review">Needs time review</option>
          </Select>
        </div>
      </div>
      {result.total ? (
        <>
          <section
            className="table-scroll tasks-table-scroll"
            aria-label="Tasks table"
            // biome-ignore lint/a11y/noNoninteractiveTabindex: Scrollable table needs keyboard access.
            tabIndex={0}
          >
            <table className="tasks-table">
              <caption className="sr-only">Tasks, most recently changed first</caption>
              <thead>
                <tr>
                  <th scope="col">Task</th>
                  <th scope="col">Status & deadline</th>
                  <th scope="col">Next step</th>
                  <th scope="col">Person</th>
                  <th scope="col">Estimate</th>
                  <th scope="col">Confirmed time</th>
                  <th scope="col">Linked context</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map(record => {
                  const task = record.value as Task;
                  const nextStep = task.steps.find(step => !step.done);
                  const totals = focusTotals(records, record.id);

                  const hasTime = records.some(
                    r =>
                      r.kind === 'focus' &&
                      (r.value as FocusSession).taskId === record.id &&
                      (r.value as FocusSession).segments.length > 0,
                  );

                  const links = (
                    [
                      ['Project', task.projectId],
                      ['Area', task.areaId],
                      ['Note', task.noteId],
                    ] as const
                  ).filter(([, id]) => id && notes.has(id));

                  return (
                    <tr key={record.id}>
                      <th scope="row">
                        <span className="task-title-cell">
                          <PebbleButton
                            stone={taskStone(record.id, task)}
                            done={task.done}
                            dropped={task.dropped}
                            title={task.title}
                            onToggle={() =>
                              void setTaskDone(record, !task.done, deviceId).catch(() => {})
                            }
                          />
                          <span>
                            <RouteLink href={taskPath(record.id)}>{task.title}</RouteLink>
                            {task.stoppingPoint && (
                              <small className="marker-quote">“{task.stoppingPoint}”</small>
                            )}
                          </span>
                        </span>
                      </th>
                      <td>
                        <TaskBadges record={record} records={records} placement />
                        {(task.done || task.dropped) && task.dueDate && (
                          <small>Due {task.dueDate}</small>
                        )}
                        {task.done && task.completedOn && (
                          <small>Completed {task.completedOn}</small>
                        )}
                      </td>
                      <td>
                        {nextStep?.text ?? (task.steps.length ? 'All steps complete' : '—')}
                        {task.steps.length > 0 && (
                          <small>
                            {task.steps.filter(step => step.done).length} / {task.steps.length}{' '}
                            steps
                          </small>
                        )}
                      </td>
                      <td>{taskPersonName(task, records) || '—'}</td>
                      <td>{task.estimateMinutes ? `${task.estimateMinutes} min` : '—'}</td>
                      <td>
                        {hasTime ? (
                          <>
                            <Button
                              variant="ghost"
                              size="compact"
                              aria-label={`Time history for ${task.title}: ${Math.round(totals.confirmed)} confirmed min`}
                              onClick={() => setReviewTaskId(record.id)}
                            >
                              {Math.round(totals.confirmed)} min
                            </Button>
                            {reviewTasks.has(record.id) && (
                              <div>
                                <StatusBadge tone="attention">Needs review</StatusBadge>
                              </div>
                            )}
                            {totals.proposed > 0 && (
                              <small>{Math.round(totals.proposed)} min proposed</small>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {links.length
                          ? links.map(([label, id]) => (
                              <div key={label}>
                                <small>{label}</small>
                                <RouteLink href={notePath(id!)}>{notes.get(id!)!.title}</RouteLink>
                              </div>
                            ))
                          : '—'}
                      </td>
                      <td>
                        <TaskActions record={record} records={records} save={save} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
          <nav className="tasks-pagination" aria-label="Task pagination">
            <span role="status">
              {(result.page - 1) * taskPageSize + 1}–
              {Math.min(result.page * taskPageSize, result.total)} of {result.total} tasks · Page{' '}
              {result.page} of {result.pages}
            </span>
            <div className="button-row">
              <Button
                variant="secondary"
                disabled={result.page === 1}
                onClick={() => setPage(result.page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                disabled={result.page === result.pages}
                onClick={() => setPage(result.page + 1)}
              >
                Next
              </Button>
            </div>
          </nav>
        </>
      ) : (
        <div className="empty-state">
          <ListTodo size={32} />
          <h2>
            {search
              ? 'No matching tasks.'
              : filter === 'all'
                ? 'No tasks yet.'
                : filter === 'review'
                  ? 'No tasks need time review.'
                  : `No ${filter} tasks.`}
          </h2>
          <p>
            {search
              ? 'Try a different title or status filter.'
              : filter === 'review'
                ? 'Tasks with unconfirmed time or overlapping timers will appear here.'
                : 'Create a task above or choose another status filter.'}
          </p>
        </div>
      )}
      {reviewTaskId && (
        <FocusHistoryDialog
          records={records}
          taskId={reviewTaskId}
          close={() => setReviewTaskId(undefined)}
        />
      )}
    </section>
  );
}

function TaskActions({
  record,
  records,
  save,
}: {
  record: LocalRecord;
  records: LocalRecord[];
  save: (record: LocalRecord, value: Task) => void;
}) {
  const [open, setOpen] = useState(false);
  const task = record.value as Task;

  const sessions = records.filter(
    r => r.kind === 'focus' && (r.value as FocusSession).taskId === record.id,
  );

  const sessionOpen = sessions.length
    ? sessions.some(r => (r.value as FocusSession).endedAt === null)
    : Boolean(task.focus?.sessionOpen);

  return (
    <ControlPopover
      size="compact"
      label={`Actions for ${task.title}`}
      open={open}
      onOpenChange={setOpen}
      trigger={
        <IconButton label={`Actions for ${task.title}`}>
          <Ellipsis aria-hidden="true" />
        </IconButton>
      }
    >
      <div className="tasks-actions">
        <ButtonLink variant="ghost" href={taskPath(record.id)}>
          Open task
        </ButtonLink>
        {task.dropped && (
          <ActionIconButton
            action="restore"
            label={`Restore commitment: ${task.title}`}
            onClick={() => {
              save(record, { ...task, dropped: false });
              setOpen(false);
            }}
          />
        )}
        <ActionIconButton
          action="trash"
          label={
            sessionOpen
              ? 'End the focus session before moving this task to trash'
              : `Move task to trash: ${task.title}`
          }
          disabled={sessionOpen}
          onClick={() => {
            save(record, { ...task, trashed: true, today: false });
            setOpen(false);
          }}
        />
      </div>
    </ControlPopover>
  );
}
